import type { z } from 'zod';
import { apiUrl } from '@/config';
import { quoteUnsafeIntegers } from '@/lib/bigIntegers';

/**
 * The only place that calls fetch. It never retries: a repeated request that places or closes orders could act twice,
 * so retrying is the caller's decision (TanStack Query retries reads only).
 */

export type ApiErrorKind =
  /** The server answered with an error status. */
  | 'http'
  /** 401 on an authenticated request: the session is no longer valid. */
  | 'unauthorized'
  /** No answer: offline, DNS, CORS, connection dropped. The request may or may not have reached the server. */
  | 'network'
  /** No answer within the time limit. The request may or may not have been processed. */
  | 'timeout'
  /** Cancelled by the caller. */
  | 'aborted'
  /** The answer did not have the expected shape. */
  | 'contract';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly body: unknown;

  constructor(kind: ApiErrorKind, message: string, status?: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.body = body;
  }

  /** True when the server may have acted on the request although no result came back. */
  get outcomeUnknown(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || (this.kind === 'http' && (this.status ?? 0) >= 500);
  }
}

export const timeouts = {
  default: 20_000,
  /** Close and add: longer than the server's 30-second close lock, so a request that waits for it is not cut off. */
  placesOrders: 45_000,
} as const;

type AuthHooks = {
  /** The value of the Authorization header, or null when signed out. */
  authorization: () => string | null;
  /** Called once for a 401 on an authenticated request. */
  onUnauthorized: () => void;
};

let authHooks: AuthHooks = { authorization: () => null, onUnauthorized: () => undefined };

/** Set by the session module, which owns the token: keeps this file free of an import cycle. */
export function setAuthHooks(hooks: AuthHooks): void {
  authHooks = hooks;
}

export type RequestOptions = {
  /** GET or POST only: IIS's WebDAV module answers PUT and DELETE with 405 before they reach the API. */
  method?: 'GET' | 'POST';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  timeoutMs?: number;
  /** False for the login request: its 401 means "wrong password", not "session expired". */
  auth?: boolean;
  signal?: AbortSignal;
};

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  const search = params.toString();

  return search ? `${url}?${search}` : url;
}

/** Empty body → undefined, JSON → parsed (integers past 2^53 as strings, see quoteUnsafeIntegers), anything else → the text. */
function parseBody(text: string, contentType: string | null): unknown {
  if (text.trim().length === 0) return undefined;
  if (contentType?.includes('html')) return undefined;

  if (contentType?.includes('json') || /^[[{"]/.test(text.trim())) {
    try {
      return JSON.parse(quoteUnsafeIntegers(text));
    } catch {
      return text;
    }
  }

  return text;
}

/** The readable message of an error body: problem details, a { message }, or plain text. */
function messageOf(body: unknown, status: number): string {
  if (typeof body === 'string' && body.trim()) return body.trim().slice(0, 2000);

  if (body && typeof body === 'object') {
    const fields = body as { detail?: unknown; title?: unknown; message?: unknown };
    for (const field of [fields.detail, fields.message, fields.title]) if (typeof field === 'string' && field.trim()) return field.trim();
  }

  return `Request failed (${status})`;
}

/** Rejects once the signal aborts: a body read is not always stopped by the fetch's own signal. */
function whenAborted(signal: AbortSignal): Promise<never> {
  return new Promise((_resolve, reject) => {
    if (signal.aborted) reject(signal.reason);
    else signal.addEventListener('abort', () => reject(signal.reason), { once: true });
  });
}

export async function request(path: string, options: RequestOptions = {}): Promise<unknown> {
  const { method = 'GET', body, query, timeoutMs = timeouts.default, auth = true, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const authorization = authHooks.authorization();
    if (authorization) headers.Authorization = authorization;
  }

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const forwardAbort = () => controller.abort();
  signal?.addEventListener('abort', forwardAbort, { once: true });

  /** Why no full answer came: the time limit, the caller, or else the connection. */
  const noAnswer = (network: string, status?: number) => {
    if (timedOut) return new ApiError('timeout', 'No answer from the server in time.');
    if (signal?.aborted) return new ApiError('aborted', 'Cancelled.');
    return new ApiError('network', network, status);
  };

  // The time limit and the caller's abort cover the body too: an answer that stops halfway must neither hang a screen
  // nor, for a close, count as failed or done.
  let response: Response;
  let text: string;
  try {
    try {
      response = await fetch(buildUrl(path, query), {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal,
      });
    } catch {
      throw noAnswer('No connection to the server.');
    }

    try {
      text = await Promise.race([response.text(), whenAborted(controller.signal)]);
    } catch {
      // The status arrived but the body did not: for a request that places orders the outcome is unknown.
      throw noAnswer('The connection dropped while the answer was arriving.', response.status);
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
  }

  const parsed = parseBody(text, response.headers.get('content-type'));

  if (response.ok) return parsed;

  if (response.status === 401 && auth) {
    authHooks.onUnauthorized();
    throw new ApiError('unauthorized', 'Your session has ended. Sign in again.', 401, parsed);
  }

  throw new ApiError('http', messageOf(parsed, response.status), response.status, parsed);
}

/** A request whose answer is checked against a schema, so a changed API fails loudly here instead of deep in a screen. */
export async function requestParsed<T extends z.ZodType>(schema: T, path: string, options?: RequestOptions): Promise<z.infer<T>> {
  const body = await request(path, options);
  const result = schema.safeParse(body);

  if (!result.success) throw new ApiError('contract', `Unexpected answer from ${path}: ${result.error.issues[0]?.message ?? 'invalid shape'}`, undefined, body);

  return result.data;
}
